import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/system';

import { Section, ContentContainer, NumberFrame } from '../../styles';
import {
  StatisticsHeading,
  StatisticsSubtextPrimary,
  StatisticsSubtextSecondary,
  StatCardContent,
  StatisticsClosingText,
  StatisticsTitle,
  StatisticsCardWrapperNew,
  StatisticsGridWrapperNew,
  StatisticsSubtitle,
  CardWrapperBox,
  LastCardGridContainer,
  LastCardWrapper,
  MotionCardContainer,
  StatisticsGridContainer,
} from './styles';
import { statisticsNew } from '../../constants';
import StatisticsCard from './StatisticsCard';
import EvidencePopover from './EvidencePopover';
import { getAnimateProps } from './utils';
import CTAButton from '../../../../../ui/ctaButton';

const StatisticsSection = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isMediumToLarge = useMediaQuery(theme.breakpoints.between('sm', 'lg'));
  const isSmallScreen = isTablet || isMobile;
  const navigateToServices = () => {
    navigate('/services');
  };

  const statisticsToShow = isSmallScreen ? statisticsNew : statisticsNew.slice(0, 9);

  const cardBaseWidth = isMediumToLarge ? 300 : 384;
  const gridGap = isMediumToLarge ? 14 : 24;

  const conditionalTruncateCards = [2, 3, 4];

  const [expandedCardIndex, setExpandedCardIndex] = useState(null);

  return (
    <Section>
      <ContentContainer>
        <StatisticsHeading>How Can I Be Sure That Hypnotherapy is Effective?</StatisticsHeading>

        <StatisticsSubtextPrimary>
          Hypnotherapy is more than just a calming experience — it's supported by decades of
          research showing how effective it can be across a wide range of challenges.
        </StatisticsSubtextPrimary>

        <StatisticsSubtextSecondary>
          At the International Association of Interpersonal Hypnotherapists (IAIH), we've compiled
          research that highlights the many areas where hypnosis has been shown to be beneficial.
        </StatisticsSubtextSecondary>

        <StatisticsGridContainer>
          <StatisticsGridWrapperNew>
            {statisticsToShow.map((stat, index) => {
              const isExpanded = expandedCardIndex === index;
              const columnsCount = isTablet ? 2 : 3;
              const columnPosition = index % columnsCount;

              return (
                <CardWrapperBox key={stat.number}>
                  <MotionCardContainer
                    initial={false}
                    animate={getAnimateProps(
                      isExpanded,
                      columnPosition,
                      isTablet,
                      isMobile,
                      cardBaseWidth,
                      gridGap
                    )}
                    transition={{
                      duration: 0.4,
                      ease: [0.4, 0, 0.2, 1],
                    }}
                    isExpanded={isExpanded}
                  >
                    <StatisticsCardWrapperNew isExpanded={isExpanded}>
                      <NumberFrame>
                        <StatisticsTitle isExpanded={isExpanded} isMobile={isMobile}>
                          {stat.title}
                        </StatisticsTitle>
                      </NumberFrame>
                      <StatCardContent isExpanded={isExpanded}>
                        <StatisticsSubtitle>{stat.subtitle}</StatisticsSubtitle>
                        <StatisticsCard
                          description={stat.description}
                          truncateAt={
                            conditionalTruncateCards.includes(stat.number)
                              ? isMediumToLarge
                                ? stat.truncateAt
                                : undefined
                              : stat.truncateAt
                          }
                          isExpanded={isExpanded}
                          onToggle={() => {
                            setExpandedCardIndex(isExpanded ? null : index);
                          }}
                        />
                        <EvidencePopover stat={stat} />
                      </StatCardContent>
                    </StatisticsCardWrapperNew>
                  </MotionCardContainer>
                </CardWrapperBox>
              );
            })}
          </StatisticsGridWrapperNew>

          {statisticsNew.length > 9 && !isSmallScreen && (
            <LastCardGridContainer>
              <LastCardWrapper>
                <StatisticsCardWrapperNew isLastCard>
                  <NumberFrame>
                    <StatisticsTitle isExpanded={false}>{statisticsNew[9].title}</StatisticsTitle>
                  </NumberFrame>
                  <StatCardContent>
                    <StatisticsSubtitle>{statisticsNew[9].subtitle}</StatisticsSubtitle>
                    <StatisticsCard description={statisticsNew[9].description} />
                    <EvidencePopover stat={statisticsNew[9]} />
                  </StatCardContent>
                </StatisticsCardWrapperNew>
              </LastCardWrapper>
            </LastCardGridContainer>
          )}
        </StatisticsGridContainer>

        <StatisticsClosingText>
          While results vary from person to person, research consistently shows hypnotherapy as a
          valuable tool for positive transformation. At Nirva, every practitioner is trained to
          deliver sessions with professionalism, compassion, and ethical care.
        </StatisticsClosingText>
        <CTAButton variant="contained" text="Explore Our Services" onClick={navigateToServices} />
      </ContentContainer>
    </Section>
  );
};

export default StatisticsSection;
